import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Hospital, Info, ArrowUpRight, ShieldCheck, MapPin } from 'lucide-react';
import { api } from '../api';

function fmtInt(n) { if (n == null) return '—'; return Number(n).toLocaleString(); }
function Star({ r }) {
  if (r == null) return <span className="text-[10.5px] text-[#A8B0AA]">no rating</span>;
  return (
    <span className="inline-flex items-center gap-0.5">
      <span className="text-[12.5px] font-semibold text-[#A06628]">{r}</span>
      <span className="text-[#D4A85C]">★</span>
    </span>
  );
}

function ScoreGauge({ score, label }) {
  const v = Math.max(0, Math.min(100, score || 0));
  const color = v >= 75 ? '#4A7C5E' : v >= 50 ? '#D4A85C' : v >= 25 ? '#D58668' : '#C75D5D';
  return (
    <div className="flex items-center gap-4">
      <svg width="92" height="92" viewBox="0 0 92 92">
        <circle cx="46" cy="46" r="36" stroke="#F0E7D2" strokeWidth="10" fill="none" />
        <circle cx="46" cy="46" r="36" stroke={color} strokeWidth="10" fill="none"
                strokeLinecap="round" strokeDasharray={2 * Math.PI * 36}
                strokeDashoffset={2 * Math.PI * 36 - (v / 100) * (2 * Math.PI * 36)}
                transform="rotate(-90 46 46)" />
        <text x="46" y="50" textAnchor="middle" style={{ fontSize: '18px', fill: '#1A2622', fontWeight: 600 }}>
          {Math.round(v)}
        </text>
      </svg>
      <div className="text-[12.5px] font-semibold text-[#1A2622]">{label}</div>
    </div>
  );
}

export default function HospitalAlignmentTab({ ccn }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.facilityHospitals(ccn, { limit: 30 })
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e?.response?.data?.detail || 'No hospital data available'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [ccn]);

  if (loading) {
    return <div className="mt-7 rounded-xl bg-white border border-[#E5DCC4] p-8 text-[13px] text-[#7A857F]" data-testid="hosp-tab-loading">Loading hospital alignment intelligence…</div>;
  }
  if (error || !data || data.count === 0) {
    return (
      <div className="mt-7 rounded-xl bg-white border border-dashed border-[#E5DCC4] p-10 text-center" data-testid="hosp-tab-empty">
        <Hospital size={28} className="text-[#34538F] mx-auto" />
        <div className="mt-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider bg-[#E0EAF8] text-[#34538F]">
          Coming Soon
        </div>
        <div className="mt-3 font-display text-xl text-[#1A2622]">Hospital Network insights</div>
        <p className="mt-2 text-[13px] text-[#5A6660] max-w-[520px] mx-auto leading-relaxed">
          We're rebuilding the hospital alignment graph from the latest CMS Facility Affiliation and Part B Provider Utilization datasets. Nearby hospitals, shared physicians, and referral overlap insights will appear here shortly.
        </p>
        <div className="mt-3 text-[11px] text-[#7A857F]">Source: CMS Facility Affiliation (27ea-46a8) + Part B PUF</div>
      </div>
    );
  }

  const s = data.summary || {};
  const items = data.items || [];

  return (
    <section className="mt-7" data-testid="hosp-tab">
      {/* Score + sub-scores */}
      <div className="rounded-xl bg-white border border-[#E5DCC4] p-5 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10">
          <ScoreGauge score={s.market_alignment_score || 0} label="Market alignment score" />
          <div className="flex-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-semibold uppercase tracking-wider bg-[#E0EAF8] text-[#34538F]">
              <Hospital size={12} /> {s.nearby_hospitals || 0} nearby hospitals · {s.confirmed_shared_physicians || 0} confirmed shared physicians
            </div>
            <p className="mt-3 text-[13px] text-[#3A4A44] leading-relaxed">
              {s.confirmed_shared_physicians || 0} physicians are <strong>confirmed</strong> (via CMS Facility Affiliation file) to round at this SNF and a nearby hospital.
              {' '}{(s.likely_shared_physicians || 0)} are <em>likely</em> shared via geographic proximity + claims overlap.
              {' '}Top hospital: <strong>{s.top_hospital || '—'}</strong>.
            </p>
            <div className="mt-4 grid grid-cols-3 md:grid-cols-5 gap-2">
              {[['Density', s.sub_scores?.density, 25],
                ['Quality', s.sub_scores?.quality, 20],
                ['Shared phys.', s.sub_scores?.shared_physicians, 30],
                ['Coverage', s.sub_scores?.specialty_coverage, 21],
                ['Diversity', s.sub_scores?.diversity, 12]].map(([k, v, max]) => (
                <div key={k}>
                  <div className="text-[10px] uppercase tracking-wider text-[#7A857F]">{k}</div>
                  <div className="text-[12.5px] font-semibold text-[#1A2622] tabular-nums">{v ?? '—'}<span className="text-[10px] text-[#A8B0AA] ml-0.5">/{max}</span></div>
                  <div className="h-1 mt-1 bg-[#F0E7D2] rounded-full overflow-hidden">
                    <div className="h-full bg-[#34538F]" style={{ width: `${Math.min(100, ((v || 0) / max) * 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { label: 'Nearby hospitals', value: s.nearby_hospitals, accent: 'text-[#34538F]' },
          { label: 'Confirmed shared', value: s.confirmed_shared_physicians, accent: 'text-[#2D5F4F]', sub: 'via CMS DAC' },
          { label: 'Likely shared', value: s.likely_shared_physicians, accent: 'text-[#A06628]', sub: 'geo overlap' },
          { label: 'Best rating', value: s.best_hospital_rating ?? '—' },
          { label: 'Hospital systems', value: s.unique_systems },
        ].map((k) => (
          <div key={k.label} className="p-3 rounded-xl bg-white border border-[#E5DCC4]">
            <div className={`font-display text-xl ${k.accent || ''}`}>{k.value ?? '—'}</div>
            <div className="text-[10.5px] uppercase tracking-wider text-[#7A857F] mt-0.5">{k.label}</div>
            {k.sub && <div className="text-[10px] text-[#A8B0AA] mt-0.5">{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* Hospitals table */}
      <div className="mt-4 rounded-xl bg-white border border-[#E5DCC4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#F0E7D2] flex items-center gap-2 flex-wrap">
          <Hospital size={14} className="text-[#34538F]" />
          <h3 className="font-display text-base font-semibold text-[#1A2622]">Nearby acute-care hospitals</h3>
          <span className="ml-auto text-[10.5px] uppercase tracking-wider text-[#7A857F]">Top {items.length} by alignment score</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead className="bg-[#F5EFDD] text-[10.5px] uppercase tracking-wider text-[#5A6660]">
              <tr>
                <th className="text-left px-4 py-2.5">Hospital</th>
                <th className="text-left px-4 py-2.5">Type / Ownership</th>
                <th className="text-left px-4 py-2.5">Rating</th>
                <th className="text-right px-4 py-2.5" title="Physicians listed on both the SNF's and the hospital's CMS Facility Affiliation records">Confirmed shared</th>
                <th className="text-right px-4 py-2.5" title="Physicians in the geographic catchment of both facilities">Likely shared</th>
                <th className="text-left px-4 py-2.5">Proximity</th>
                <th className="text-right px-4 py-2.5">Score</th>
              </tr>
            </thead>
            <tbody>
              {items.map((h) => (
                <tr key={`${h.ccn_hospital}-${h.reason}`} className="border-t border-[#F0E7D2] hover:bg-[#FBF6EC]" data-testid={`hosp-row-${h.ccn_hospital}`}>
                  <td className="px-4 py-2.5">
                    <Link to={`/hospitals/${h.ccn_hospital}`} className="text-[#1A2622] font-medium hover:text-[#34538F]">
                      {h.hospital_name}
                    </Link>
                    <div className="text-[10.5px] text-[#7A857F] flex items-center gap-1">
                      <MapPin size={10} /> {h.hospital_city || ''}{h.hospital_city && h.hospital_state ? ', ' : ''}{h.hospital_state || ''} · CCN {h.ccn_hospital}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-[#3A4A44]">
                    <div className="truncate max-w-[200px]" title={h.hospital_type}>{h.hospital_type || '—'}</div>
                    <div className="text-[10.5px] text-[#7A857F] truncate max-w-[200px]" title={h.hospital_ownership}>{h.hospital_ownership || ''}</div>
                  </td>
                  <td className="px-4 py-2.5"><Star r={h.hospital_rating} /></td>
                  <td className="px-4 py-2.5 text-right">
                    <span className="font-semibold text-[#2D5F4F] tabular-nums">{fmtInt(h.confirmed_shared_physicians)}</span>
                    {h.confirmed_shared_physicians > 0 && (
                      <div className="text-[10px] text-[#7A857F]">
                        {h.confirmed_shared_pm_r ? `PM&R ${h.confirmed_shared_pm_r} ` : ''}
                        {h.confirmed_shared_internal_med ? `IM ${h.confirmed_shared_internal_med} ` : ''}
                        {h.confirmed_shared_app ? `APP ${h.confirmed_shared_app}` : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <span className="text-[#A06628] tabular-nums">{fmtInt(h.likely_shared_physicians)}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${h.confidence === 'high' ? 'bg-[#E5F0EA] text-[#2D5F4F]' : h.confidence === 'medium' ? 'bg-[#FBE6CC] text-[#A06628]' : 'bg-[#E5DCC4] text-[#5A6660]'}`}>
                      {h.confidence}
                    </span>
                    <div className="text-[10.5px] text-[#7A857F] mt-0.5">
                      {h.distance_mi != null ? `${h.distance_mi} mi` : h.reason?.replace(/_/g, ' ')}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <span className="inline-block font-semibold text-[#34538F]">{h.alignment_score?.toFixed(0)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2 items-center">
        <Link to="/hospitals" className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-md bg-[#1A2622] text-[#FBF6EC] text-[12.5px] hover:bg-[#34538F]" data-testid="hosp-link-directory">
          Hospital directory <ArrowUpRight size={13} />
        </Link>
        <span className="inline-flex items-center gap-1.5 px-2.5 h-9 rounded-md bg-[#E5F0EA] text-[#2D5F4F] text-[11.5px] font-semibold">
          <ShieldCheck size={12} /> Public CMS data · no patient referrals
        </span>
      </div>

      <div className="mt-5 flex items-start gap-2 text-[11.5px] text-[#A8B0AA] leading-relaxed">
        <Info size={12} className="mt-0.5 shrink-0" />
        <span>
          <strong>Methodology:</strong> {s.methodology}
          {' '}<strong>Source:</strong> {data.source}.
          {s.updated_at && <> Updated {new Date(s.updated_at).toLocaleDateString()}.</>}
        </span>
      </div>
    </section>
  );
}
